// HiPPO Insight Page - Score Hero + 3-Folder Stack
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Home, Share2, Lock, Shield,
  Bookmark, MapPin, TrendingUp,
  Twitter, Facebook, Link2, Check, Maximize2, Building2, BarChart3
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import html2canvas from "html2canvas";
import { ScoreExplanationModal } from "@/components/ScoreExplanationModal";
import { ErrorState } from "@/components/ErrorState";
import { ThemeToggle } from "@/components/ThemeProvider";
import { feedback, haptic } from "@/utils/feedback";
import ExportableInsightCard from "@/components/ExportableInsightCard";
import PropertySchematic from "@/components/PropertySchematic";

// Extracted components
import {
  Confetti, ScoreDetailModal, InsightSkeleton, ScoreMiniRing,
  FolderStack, PUBLIC_FOLDERS, DEFAULT_FOLDERS,
  WhatsPossible, WhatsItWorth, WhatToDoNext,
  WhatsDriving, WhatItMeans, WhatsNext,
  PartnerBadge, AgentBrief, BudgetIntegrity, ShareWithClient,
  SectionZeroHero, AreaComparison, WhatIfSimulator, OpportunityCarousel, ActionFolder, FinancialSummary
} from "@/components/hippo";
import {
  InvestorBadge, InvestorSummary, YieldComparison, ROIProjection, InvestmentRisk
} from "@/components/investor/InvestorSections";
import {
  HERO_IMAGE, BAND_CONFIG, DWELLING_NAMES, GOAL_OPPORTUNITIES
} from "@/pages/hippo/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== Helpers ==============
const getBedroomText = (sessionData) => {
  const bedrooms = sessionData?.bedrooms;
  if (!bedrooms) return '';
  if (bedrooms === '1' || bedrooms === 1) return '1 Bed';
  if (bedrooms === '4' || bedrooms === '4_plus' || bedrooms >= 4) return '4+ Bed';
  return `${bedrooms} Bed`;
};

const getAreaName = (sessionData) => {
  const postcode = sessionData?.postcode;
  if (!postcode) return 'Your Area';
  const outward = postcode.split(' ')[0];
  const areaMap = {
    'SW1': 'Westminster', 'SW2': 'Brixton', 'SW3': 'Chelsea', 'SW4': 'Clapham',
    'W1': 'West End', 'W2': 'Paddington', 'EC1': 'City', 'EC2': 'City',
    'SE1': 'Southwark', 'N1': 'Islington', 'E1': 'Whitechapel', 'NW1': 'Camden',
    'M1': 'Manchester', 'M2': 'Manchester', 'B1': 'Birmingham', 'B2': 'Birmingham',
    'L1': 'Liverpool', 'G1': 'Glasgow', 'EH1': 'Edinburgh', 'CF1': 'Cardiff',
    'BS1': 'Bristol', 'LS1': 'Leeds', 'S1': 'Sheffield', 'NG1': 'Nottingham',
    'SO1': 'Southampton', 'SO14': 'Southampton', 'SO15': 'Southampton', 'SO16': 'Southampton',
    'PO1': 'Portsmouth', 'BN1': 'Brighton', 'OX1': 'Oxford', 'CB1': 'Cambridge'
  };
  return areaMap[outward] || areaMap[outward.slice(0, 3)] || areaMap[outward.slice(0, 2)] || outward;
};

const getDwellingName = (sessionData) => DWELLING_NAMES[sessionData?.dwelling_type] || "property";
const getOpportunityType = (sessionData) => GOAL_OPPORTUNITIES[sessionData?.main_goal] || "value enhancement";

// ============== Main Component ==============
export default function HiPPOInsightPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const exportCardRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [hippoData, setHippoData] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [paymentChecking, setPaymentChecking] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scoreModal, setScoreModal] = useState({ open: false, type: null, score: 0 });
  const [showConfetti, setShowConfetti] = useState(false);
  const [gaugeComplete, setGaugeComplete] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [unlockSheetExpanded, setUnlockSheetExpanded] = useState(false);
  const [activeFolder, setActiveFolder] = useState(null);
  const [showSimulator, setShowSimulator] = useState(false);

  const openScoreModal = (scoreType, value) => setScoreModal({ open: true, type: scoreType, score: value });
  const closeScoreModal = () => setScoreModal({ open: false, type: null, score: 0 });

  // Poll Stripe payment status
  const pollPaymentStatus = useCallback(async (stripeSessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error("Payment verification timed out. Please check your email for confirmation.");
      setPaymentChecking(false);
      return;
    }
    setPaymentChecking(true);
    try {
      const response = await axios.get(`${API}/hippo/payment/status/${stripeSessionId}`);
      if (response.data.is_paid) {
        const token = accessToken || sessionStorage.getItem(`hippo_token_${submissionId}`);
        if (token) {
          const fullResponse = await axios.get(`${API}/hippo/full`, {
            params: { submission_id: submissionId, token }
          });
          setHippoData(fullResponse.data);
          setIsUnlocked(true);
          sessionStorage.setItem(`hippo_data_${submissionId}`, JSON.stringify(fullResponse.data));
          feedback.unlock();
          toast.success("Payment successful! Full insight unlocked!");
        }
        setPaymentChecking(false);
        return;
      } else if (response.data.status === 'expired') {
        toast.error("Payment session expired. Please try again.");
        setPaymentChecking(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(stripeSessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Payment status check error:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(stripeSessionId, attempts + 1), 2000);
      } else {
        toast.error("Error verifying payment. Please contact support if payment was taken.");
        setPaymentChecking(false);
      }
    }
  }, [submissionId, accessToken]);

  // Check for payment success from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const stripeSessionId = urlParams.get('session_id');
    if (paymentStatus === 'success' && stripeSessionId) {
      pollPaymentStatus(stripeSessionId);
      window.history.replaceState({}, '', `/hippo/${submissionId}`);
    } else if (paymentStatus === 'cancelled') {
      toast.info("Payment cancelled. You can try again anytime.");
      window.history.replaceState({}, '', `/hippo/${submissionId}`);
    }
  }, [submissionId, pollPaymentStatus]);

  // Load data from sessionStorage
  useEffect(() => {
    const storedToken = sessionStorage.getItem(`hippo_token_${submissionId}`);
    if (storedToken) setAccessToken(storedToken);
    const storedSession = sessionStorage.getItem(`hippo_session_${submissionId}`);
    if (storedSession) setSessionData(JSON.parse(storedSession));
    const storedHippo = sessionStorage.getItem(`hippo_data_${submissionId}`);
    if (storedHippo) {
      const data = JSON.parse(storedHippo);
      setHippoData(data);
      setIsUnlocked(data.meta?.mode === "full");
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [submissionId]);

  const handleGaugeComplete = () => {
    setGaugeComplete(true);
    setShowConfetti(true);
    feedback.success();
    setTimeout(() => setShowCard(true), 200);
  };

  const handleUnlock = async () => {
    if (!accessToken) { toast.error("Access token not found. Please try again."); return; }
    setUnlockLoading(true);
    feedback.tap();
    try {
      const unlockResponse = await axios.post(`${API}/hippo/unlock/${submissionId}`);
      if (unlockResponse.data.status === 'unlocked' || unlockResponse.data.status === 'already_paid') {
        const response = await axios.get(`${API}/hippo/full`, {
          params: { submission_id: submissionId, token: accessToken }
        });
        setHippoData(response.data);
        setIsUnlocked(true);
        sessionStorage.setItem(`hippo_data_${submissionId}`, JSON.stringify(response.data));
        feedback.unlock();
        toast.success("Full insight unlocked!");
      }
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Payment required')) {
        try {
          const checkoutResponse = await axios.post(`${API}/hippo/checkout`, {
            submission_id: submissionId, origin_url: window.location.origin
          });
          if (checkoutResponse.data.checkout_url) {
            toast.info("Redirecting to secure checkout...");
            window.location.href = checkoutResponse.data.checkout_url;
            return;
          }
        } catch (checkoutError) {
          console.error('Checkout error:', checkoutError);
          toast.error("Failed to start checkout. Please try again.");
        }
      } else {
        toast.error("Failed to unlock insight. Please try again.");
      }
    } finally {
      setUnlockLoading(false);
    }
  };

  const handleSave = async () => {
    if (!exportCardRef.current && !cardRef.current) return;
    setSaving(true);
    feedback.tap();
    try {
      const targetElement = exportCardRef.current || cardRef.current;
      const canvas = await html2canvas(targetElement, { 
        backgroundColor: "#1E1E1E", scale: 2, useCORS: true, width: 400, height: 400
      });
      const link = document.createElement("a");
      link.download = `cipher-insight-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      feedback.success();
      toast.success("Saved to your library", { description: "You can revisit or update this insight anytime." });
    } catch (error) {
      toast.error("Failed to save image");
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    if (exportCardRef.current) {
      try {
        const canvas = await html2canvas(exportCardRef.current, { 
          backgroundColor: "#1E1E1E", scale: 2, useCORS: true, width: 400, height: 400
        });
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        if (navigator.share && navigator.canShare) {
          const file = new File([blob], 'cipher-insight.png', { type: 'image/png' });
          const shareData = {
            title: "My Property Intelligence - HiPPO",
            text: `I discovered my property has ${hippoData?.hippo_band} potential with HiPPO by StudioNPG!`,
            files: [file]
          };
          if (navigator.canShare(shareData)) { await navigator.share(shareData); return; }
        }
      } catch (err) { console.warn('Image share failed, falling back to URL share'); }
    }
    const shareData = {
      title: "My Property Intelligence - HiPPO",
      text: `I discovered my property has ${hippoData?.hippo_band} potential with HiPPO by StudioNPG!`,
      url: window.location.href
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (err) {
        if (err.name !== "AbortError") setShowShareMenu(true);
      }
    } else { setShowShareMenu(true); }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { console.warn('Clipboard access not available'); }
  };

  const socialLinks = [
    { name: "Twitter", icon: Twitter, action: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`I discovered my property has ${hippoData?.hippo_band} potential!`)}&url=${encodeURIComponent(window.location.href)}`, "_blank") },
    { name: "Facebook", icon: Facebook, action: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, "_blank") },
    { name: "Copy Link", icon: copied ? Check : Link2, action: handleCopyLink }
  ];

  const bandConfig = BAND_CONFIG[hippoData?.hippo_band] || BAND_CONFIG["Medium"];

  if (loading) return <InsightSkeleton />;

  if (!hippoData) {
    return (
      <div className="fixed inset-0 flex flex-col overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 backdrop-blur-sm bg-black/60" />
        </div>
        <header className="relative z-10 p-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-3 py-2 rounded-full">
            <div className="w-6 h-6 rounded-md bg-cipher-green flex items-center justify-center">
              <Home className="w-3 h-3 text-white" />
            </div>
            <span className="font-bold text-sm text-white">Cipher</span>
          </button>
        </header>
        <main className="relative z-10 flex-1 flex items-center justify-center p-4">
          <ErrorState type="notFound" customTitle="Insight Not Found" customMessage="We couldn't find this insight." />
        </main>
      </div>
    );
  }

  const { hippo_band, scores, confidence, ceiling_caution, signals, meta, context } = hippoData;
  const overallScore = hippoData?.unified_score ?? Math.round((scores.spatial_efficiency + scores.unlockability + scores.market_alignment) / 3);
  const visibleSignals = signals?.slice(0, 1) || [];
  const lockedSignals = signals?.slice(1, 4) || [];
  const hasConservationBadge = context?.is_conservation_area === true;
  const conservationBadge = context?.conservation_badge;
  const floorAreaConfidence = context?.floor_area_confidence;
  
  // Partner lens detection
  const partnerData = hippoData?.partner;
  const isPartnerLens = !!partnerData?.is_partner;
  
  // Investor lens detection
  const investorData = hippoData?.investor;
  const isInvestorLens = !!investorData?.is_investor;
  const investorContext = sessionStorage.getItem(`investor_context_${submissionId}`);
  const parsedInvestorContext = investorContext ? JSON.parse(investorContext) : null;

  return (
    <>
    <div className="fixed inset-0 flex flex-col overflow-auto">
      <Confetti show={showConfetti} />
      <ScoreExplanationModal
        isOpen={scoreModal.open}
        onClose={() => setScoreModal({ open: false, type: null, score: 0 })}
        scoreType={scoreModal.type}
        currentScore={scoreModal.score}
      />
      
      {/* Full-bleed hero background */}
      <div className="fixed inset-0 -z-10">
        <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 backdrop-blur-sm bg-black/60" />
      </div>

      {/* No header — start-another button is in card footer */}

      {/* Main Content */}
      <main className="flex-1 flex flex-col p-4 pb-24 max-w-lg mx-auto w-full">

        {/* Insight Card */}
        <div className={`transition-all duration-700 mt-4 ${showCard ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} ref={cardRef}>
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl overflow-hidden shadow-2xl" data-testid="hippo-insight-card">
            <div className="p-6">
              {/* Property Insight Header */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-xs text-white/50 uppercase tracking-wider">
                      {isInvestorLens ? 'Investment Analysis' : isPartnerLens ? 'Partner Assessment' : 'Property Intelligence'}
                    </p>
                    <span className="text-[8px] text-cipher-gold/30">HiPPO by StudioNPG</span>
                  </div>
                  <p className="text-sm font-medium text-white">
                    {sessionData?.first_name || 'Your Property'} <span className="text-white/40">|</span> <span className="text-white/70">{getAreaName(sessionData)}</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-cipher-gold">
                    {getBedroomText(sessionData)} {getDwellingName(sessionData)}
                  </p>
                  <p className="text-[10px] text-white/30 mt-0.5">{new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
              </div>

              {/* ===== SECTION 0: Score Hero — Full Hook & Teaser ===== */}
              <SectionZeroHero
                hippo_band={hippo_band}
                overallScore={overallScore}
                valueHeadroomRange={hippoData?.value_headroom_range}
                strategies={hippoData?.value_headroom?.strategies}
                postcode={sessionData?.postcode}
                dwellingName={getDwellingName(sessionData)}
                fallbackGoal={getOpportunityType(sessionData)}
                gaugeComplete={gaugeComplete}
                onGaugeComplete={handleGaugeComplete}
                percentile={(() => {
                  const band = hippo_band;
                  const s = overallScore || 55;
                  if (band === "Exceptional") return Math.min(96, 85 + Math.round(s / 10));
                  if (band === "High") return Math.min(82, 60 + Math.round(s / 5));
                  if (band === "Medium") return Math.min(60, 35 + Math.round(s / 4));
                  return Math.min(35, 10 + Math.round(s / 5));
                })()}
              />

              {/* Partner Lens Sections */}
              {isPartnerLens && partnerData?.agent_brief && (
                <AgentBrief brief={partnerData.agent_brief} clientType={partnerData.context?.client_type} />
              )}
              {isPartnerLens && partnerData?.budget_integrity && (
                <BudgetIntegrity budgetCheck={partnerData.budget_integrity} />
              )}
              {isPartnerLens && (
                <ShareWithClient submissionId={submissionId} />
              )}

              {/* Investor Lens Sections */}
              {isInvestorLens && investorData?.analysis && (
                <InvestorSummary analysis={investorData.analysis} investorContext={parsedInvestorContext} />
              )}
              {isInvestorLens && investorData?.analysis?.yield_comparison && (
                <YieldComparison analysis={investorData.analysis} />
              )}
              {isInvestorLens && investorData?.analysis?.roi_projection && (
                <ROIProjection analysis={investorData.analysis} investorContext={parsedInvestorContext} />
              )}
              {isInvestorLens && investorData?.analysis?.risk_assessment && (
                <InvestmentRisk analysis={investorData.analysis} />
              )}

              {/* ===== DOSSIER ACCORDION (Public Lens) / FOLDER STACK (Other Lenses) ===== */}
              {!isPartnerLens && !isInvestorLens ? (
                <div className="relative">
                  {/* Emotional hook stat */}
                  {!isUnlocked && hippoData?.value_headroom_range && (
                    <div className="text-center py-3 mb-2" data-testid="emotional-hook">
                      <p className="text-xs text-white/50 leading-relaxed">
                        Did you know? Similar <span className="text-white/70">{getDwellingName(sessionData)}s</span> in{' '}
                        <span className="text-white/70">{getAreaName(sessionData)}</span> that acted on their potential saw up to{' '}
                        <span className="text-cipher-green font-semibold">
                          {(() => {
                            const pct = hippoData.value_headroom?.strategies?.[0]?.percentage_range;
                            return pct ? `${pct.upper}%` : '25%';
                          })()}
                        </span>{' '}
                        value increase.
                      </p>
                    </div>
                  )}

                  {/* Simulator prompt — visible when unlocked */}
                  {isUnlocked && (
                    <button
                      onClick={() => setShowSimulator(true)}
                      className="w-full flex items-center gap-3 px-4 py-3 mb-3 bg-gradient-to-r from-cipher-gold/[0.08] to-cipher-green/[0.06] border border-cipher-gold/20 rounded-xl hover:from-cipher-gold/[0.12] hover:to-cipher-green/[0.10] transition-all"
                      data-testid="simulator-trigger"
                    >
                      <div className="w-8 h-8 rounded-lg bg-cipher-gold/15 flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="w-4 h-4 text-cipher-gold" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-medium text-white/80">Try the What If Simulator</p>
                        <p className="text-[10px] text-white/40">Toggle strategies on and off to see how your potential changes</p>
                      </div>
                    </button>
                  )}

                  <FolderStack
                    activeFolder={activeFolder}
                    onFolderChange={(id) => {
                      if (id === "driving" || isUnlocked) {
                        setActiveFolder(activeFolder === id ? null : id);
                      }
                    }}
                    folders={PUBLIC_FOLDERS.map(f => {
                      if (f.id === "meaning" && !isUnlocked && hippoData?.value_headroom) {
                        const stratCount = (hippoData.value_headroom.strategies?.length || 0) + (hippoData.value_headroom.supportive_strategies?.length || 0);
                        return { ...f, teaser: `${stratCount} strategies identified • Net return potential` };
                      }
                      if (f.id === "next" && !isUnlocked) {
                        return { ...f, teaser: "Priority ranking • Timeline • Expert consultation" };
                      }
                      return f;
                    })}
                    disabled={false}
                    lockedFolders={isUnlocked ? [] : ["meaning", "next"]}
                  >
                  {{
                    driving: (
                      <div className="space-y-4">
                        {/* Contextual badges */}
                        <div className="flex flex-wrap gap-2">
                          {hasConservationBadge && (
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/20" data-testid="conservation-badge">
                              <Shield className="w-3 h-3 text-amber-400/70" />
                              <span className="text-[10px] font-medium text-amber-400/70">{conservationBadge || "Conservation Area"}</span>
                            </div>
                          )}
                          {floorAreaConfidence === "high" && (
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-blue-500/15 border border-blue-500/20" data-testid="floor-area-badge">
                              <Maximize2 className="w-3 h-3 text-blue-400/70" />
                              <span className="text-[10px] font-medium text-blue-400/70">Local floor data</span>
                            </div>
                          )}
                        </div>

                        {/* Quadrant room breakdown with narrative */}
                        {sessionData && (
                          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3" data-testid="schematic-view">
                            <p className="text-[10px] text-white/30 uppercase tracking-wider text-center mb-1">Where your potential sits</p>
                            <p className="text-[10px] text-white/40 text-center mb-3 leading-relaxed">
                              Based on your {getDwellingName(sessionData)}'s layout and features, here's where the opportunities concentrate
                            </p>
                            <PropertySchematic
                              dwellingType={sessionData.dwelling_type}
                              features={sessionData.features || []}
                              separateKitchen={sessionData.separate_kitchen}
                              bedrooms={sessionData.bedrooms}
                            />
                          </div>
                        )}

                        {/* Strategy teasers — show least valuable clearly, blur most valuable */}
                        {!isUnlocked && hippoData?.value_headroom?.strategies && (() => {
                          const all = [...(hippoData.value_headroom.strategies || []), ...(hippoData.value_headroom.supportive_strategies || [])].slice(0, 4);
                          // Sort: least valuable first (lowest % range)
                          const sorted = [...all].sort((a, b) => (a.percentage_range?.upper || 0) - (b.percentage_range?.upper || 0));
                          return (
                            <div data-testid="strategy-teasers">
                              <p className="text-[10px] text-white/40 uppercase tracking-wider mb-2">Strategies identified</p>
                              <div className="space-y-1.5">
                                {sorted.map((s, i) => {
                                  const isLast = i === sorted.length - 1;
                                  const isBlurred = i > 0; // Only first (least valuable) is clear
                                  return (
                                    <div key={s.id} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5 ${isBlurred ? 'blur-[3px]' : ''}`}>
                                      <Lock className="w-3 h-3 text-white/20 flex-shrink-0" />
                                      <span className="text-xs text-white/50">{s.label}</span>
                                      <span className="ml-auto text-[9px] text-cipher-green/50 font-medium">{s.percentage_range?.lower}–{s.percentage_range?.upper}%</span>
                                    </div>
                                  );
                                })}
                              </div>
                              <p className="text-[10px] text-cipher-green/50 text-center mt-2 font-medium">Unlock to reveal your highest-value strategy</p>
                            </div>
                          );
                        })()}
                      </div>
                    ),
                    meaning: (
                      <div className="space-y-3">
                        {/* Opportunity Carousel */}
                        {scores && (
                          <OpportunityCarousel scores={scores} />
                        )}

                        {/* Signals detail */}
                        <WhatsDriving
                          signals={hippoData?.signals?.slice(0, 3) || []}
                          floorAreaInsight={hippoData?.floor_area_insight}
                          lens="public"
                          sessionData={sessionData}
                        />

                        {/* Combined financial summary */}
                        <FinancialSummary
                          valueHeadroom={hippoData?.value_headroom}
                          valueHeadroomRange={hippoData?.value_headroom_range}
                        />

                        {/* Strategy cards */}
                        <WhatItMeans
                          valueHeadroom={hippoData?.value_headroom}
                          isUnlocked={isUnlocked}
                          ceilingCaution={ceiling_caution}
                          currentScore={overallScore}
                          comparisonContext={hippoData?.comparison_context}
                          valueHeadroomRange={hippoData?.value_headroom_range}
                        />
                      </div>
                    ),
                    next: (
                      <ActionFolder
                        valueHeadroom={hippoData?.value_headroom}
                        isUnlocked={isUnlocked}
                      />
                    ),
                  }}
                </FolderStack>
                </div>
              ) : (
                <FolderStack
                  activeFolder={activeFolder}
                  onFolderChange={setActiveFolder}
                  folders={DEFAULT_FOLDERS}
                >
                  {{
                    possible: (
                      <WhatsPossible
                        signals={hippoData?.signals?.slice(0, 3) || []}
                        floorAreaInsight={hippoData?.floor_area_insight}
                        lens={isInvestorLens ? "investor" : "partner"}
                      />
                    ),
                    worth: (
                      <WhatsItWorth
                        valueHeadroom={hippoData?.value_headroom}
                        isUnlocked={isUnlocked}
                        ceilingCaution={ceiling_caution}
                      />
                    ),
                    next: (
                      <WhatToDoNext
                        valueHeadroom={hippoData?.value_headroom}
                        isUnlocked={isUnlocked}
                        confidence={confidence}
                      />
                    ),
                  }}
                </FolderStack>
              )}

              {/* Card Footer — Save/Share (unlocked) + Start Another */}
              <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                <button
                  onClick={() => navigate("/")}
                  className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors border border-white/10 text-white/50 text-xs"
                  data-testid="start-another-btn"
                >
                  <Home className="w-3 h-3" />
                  <span>New Assessment</span>
                </button>
                {isUnlocked && (
                  <div className="flex items-center gap-2">
                    <button onClick={handleSave} disabled={saving}
                      className="flex items-center gap-2 px-4 py-2 rounded-full bg-cipher-green hover:bg-cipher-green/90 transition-colors text-white text-sm font-medium"
                      data-testid="save-card-btn"
                    >
                      <Bookmark className="w-4 h-4" /><span>Save</span>
                    </button>
                    <button onClick={handleShare}
                      className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors border border-white/10 text-white/80 text-sm"
                      data-testid="share-card-btn"
                    >
                      <Share2 className="w-4 h-4" /><span>Share</span>
                    </button>
                  </div>
                )}
              </div>
              
              {/* Integrity Footer — StudioNPG branding */}
              <div className="mt-4 pt-3 border-t border-white/5 text-center">
                <p className="text-[10px] text-white/30 leading-relaxed">Generated using local context, property patterns, and your inputs.</p>
                <p className="text-[10px] text-white/25 mt-1">Insight is directional and not a valuation.</p>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <img src="https://customer-assets.emergentagent.com/job_4f723f94-5de1-433e-a1fc-17c2a953d5fd/artifacts/3r4ezkw8_Nehemiah%20Profile%201.png" alt="StudioNPG" className="w-5 h-5 rounded" />
                  <p className="text-[10px] text-white/20">Brought to you by <span className="text-cipher-gold/40 font-medium">StudioNPG</span></p>
                </div>
              </div>
            </div>
          </div>

          {/* Hidden Exportable Card */}
          <div className="fixed -left-[9999px] top-0" aria-hidden="true">
            <ExportableInsightCard ref={exportCardRef} band={hippoData?.hippo_band} signals={hippoData?.signals || []} dwellingType={sessionData?.dwelling_type} firstName={sessionData?.first_name} />
          </div>

          {/* Share Menu */}
          {showShareMenu && (
            <div className="mt-2 backdrop-blur-xl bg-black/80 rounded-xl shadow-lg border border-white/20 p-2">
              {socialLinks.map((social) => (
                <button key={social.name} onClick={() => { social.action(); setShowShareMenu(false); }}
                  className="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <social.icon className="w-4 h-4 text-white/60" />
                  <span className="text-sm text-white/80">{social.name}</span>
                </button>
              ))}
              <button onClick={() => setShowShareMenu(false)} className="w-full px-3 py-2 text-xs text-white/40 hover:text-white/60">Close</button>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className={`relative z-10 p-4 text-center ${!isUnlocked ? 'mb-24' : ''}`}>
        <p className="text-xs text-white/30">Observational signals • Not financial advice</p>
      </footer>

      {/* Score Detail Modal */}
      <ScoreDetailModal isOpen={scoreModal.open} onClose={closeScoreModal} scoreType={scoreModal.type} value={scoreModal.score} />
    </div>

    {/* What If Simulator — dedicated overlay */}
    {showSimulator && (
      <>
        <div className="fixed inset-0 z-[9990] bg-black/60 backdrop-blur-sm" onClick={() => setShowSimulator(false)} />
        <div className="fixed inset-x-0 bottom-0 z-[9991] max-h-[85vh] overflow-y-auto">
          <div className="max-w-lg mx-auto bg-[#1a1a1a]/98 border-t border-x border-white/20 rounded-t-3xl shadow-2xl">
            <div className="flex justify-center pt-3 pb-2 cursor-pointer" onClick={() => setShowSimulator(false)}>
              <div className="w-12 h-1.5 rounded-full bg-white/30" />
            </div>
            <div className="px-5 pb-6">
              <WhatIfSimulator
                valueHeadroom={hippoData?.value_headroom}
                currentScore={overallScore}
                band={hippo_band}
                valueHeadroomRange={hippoData?.value_headroom_range}
              />
            </div>
          </div>
        </div>
      </>
    )}

    {/* Unlock Bottom Sheet — outside the fixed container for reliable z-index */}
    {!isUnlocked && (
      <>
        {unlockSheetExpanded && (
          <div 
            className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setUnlockSheetExpanded(false)}
          />
        )}
        <div className="fixed bottom-0 left-0 right-0 z-[9999]" data-testid="unlock-sheet-wrapper">
          <div className={`mx-auto max-w-lg ${unlockSheetExpanded ? 'px-0' : 'px-4 pb-4'}`}>
            {!unlockSheetExpanded && (
              <div 
                className="backdrop-blur-xl bg-[#1a1a1a]/95 border border-white/20 rounded-2xl shadow-2xl overflow-hidden cursor-pointer"
                onClick={() => setUnlockSheetExpanded(true)}
                data-testid="unlock-collapsed-sheet"
              >
                <div className="flex justify-center pt-2 pb-1">
                  <div className="w-10 h-1 rounded-full bg-white/20" />
                </div>
                <div className="px-4 pb-4">
                  <button
                    className="w-full bg-cipher-green hover:bg-cipher-green/90 text-white py-4 text-base font-medium rounded-xl shadow-lg shadow-cipher-green/30 transition-all flex items-center justify-center gap-2"
                    data-testid="unlock-btn"
                  >
                    <span>Unlock Full Intelligence</span>
                    <span className="text-white/70">—</span>
                    <span className="font-bold">£29</span>
                  </button>
                </div>
              </div>
            )}

            {unlockSheetExpanded && (
              <div className="backdrop-blur-xl bg-[#1a1a1a]/98 border-t border-x border-white/20 rounded-t-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300">
                <div className="flex justify-center pt-3 pb-2 cursor-pointer" onClick={() => setUnlockSheetExpanded(false)}>
                  <div className="w-12 h-1.5 rounded-full bg-white/30 hover:bg-white/40 transition-colors" />
                </div>
                <div className="px-6 pb-8 max-h-[70vh] overflow-y-auto">
                  <h2 className="text-xl font-bold text-white text-center mb-3">
                    Unlock Your Full Intelligence
                  </h2>

                  {/* Combined description + social proof */}
                  {hippoData?.value_headroom_range ? (
                    <p className="text-sm text-white/70 text-center mb-4 leading-relaxed">
                      Similar <span className="text-white font-medium">{getDwellingName(sessionData)}s</span> in{' '}
                      <span className="text-white font-medium">{getAreaName(sessionData)}</span> have shown up to{' '}
                      <span className="text-cipher-green font-bold">£{Math.round(hippoData.value_headroom_range.max / 1000)}k</span>{' '}
                      in unlockable potential — see your scores, strategies, and next steps.
                    </p>
                  ) : (
                    <p className="text-sm text-white/70 text-center mb-4 leading-relaxed">
                      See what's possible within your existing footprint — scores, strategies, and next steps.
                    </p>
                  )}

                  {/* Trust Anchors */}
                  <div className="flex items-center justify-center gap-2 mb-5 flex-wrap">
                    <span className="text-[11px] text-white/50 flex items-center gap-1">
                      <Home className="w-3 h-3 text-cipher-gold/70" />
                      Footprint-first analysis
                    </span>
                    <span className="text-white/20">·</span>
                    <span className="text-[11px] text-white/50 flex items-center gap-1">
                      <Shield className="w-3 h-3 text-cipher-gold/70" />
                      Based on real property data
                    </span>
                    <span className="text-white/20">·</span>
                    <span className="text-[11px] text-white/50">No estate agent bias</span>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 mb-5 border border-white/10">
                    <p className="text-xs text-white/50 uppercase tracking-wider mb-3">Includes:</p>
                    <ul className="space-y-2.5">
                      {["Full score breakdown with confidence level", "Personalised improvement strategies", "What's driving your score and what it means", "Footprint-first action plan with timelines"].map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full bg-cipher-green/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-cipher-green" />
                          </div>
                          <span className="text-sm text-white/80">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button
                    onClick={handleUnlock}
                    disabled={unlockLoading}
                    className="w-full bg-cipher-green hover:bg-cipher-green/90 text-white py-5 text-lg font-medium rounded-xl shadow-lg shadow-cipher-green/30 transition-all hover:shadow-xl hover:shadow-cipher-green/40 mb-4"
                    data-testid="unlock-expanded-btn"
                  >
                    {unlockLoading ? (
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <span>Unlock Now</span>
                        <span className="mx-2 text-white/50">—</span>
                        <span className="font-bold">£29</span>
                      </>
                    )}
                  </Button>
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <p className="text-[10px] text-white/50">Intelligence available for 30 days</p>
                    <span className="text-white/20">|</span>
                    <p className="text-[10px] text-cipher-green/60 font-medium">Money-back guarantee</p>
                  </div>
                  <p className="text-[10px] text-white/35 text-center leading-relaxed mb-1">
                    Join homeowners who've already taken the first step.
                  </p>
                  <p className="text-[10px] text-white/25 text-center leading-relaxed">
                    Not useful? Full refund, no questions asked.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    )}
    </>
  );
}
